import React, { useMemo } from 'react';
import { planningLevel } from '../nav/planningLevel';
import Tabs from '../components/Tabs';
import { usePersistentValue } from '../hooks/usePersistentState';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import ProfitLossPage from './ProfitLossPage';
import ForecastBalanceSheetTab, { BS_FOCUS } from './ForecastBalanceSheetTab';
import CashFlowPage from './CashFlowPage';
import ForecastCashPlanTab from './ForecastCashPlanTab';
import InvestAmortPanel from './financials/InvestAmortPanel';
import PurchasesMonthlyCard from './financials/PurchasesMonthlyCard';
import VatYearPage from './financials/VatYearPage';
import ForecastSocialTab from './ForecastSocialTab';
import CashMovesPage from './financials/CashMovesPage';

/* Construction du prévisionnel — une ÉTAPE = une section du menu.

   Même grammaire que Planification : construire suit un chemin numéroté, lire
   se fait dans un hub à onglets. Chaque étape n'expose que le bloc de charges ou
   de produits qu'elle traite — le filtrage par section existait déjà dans
   `ProfitLossPage` (`sectionFilter` / `subtotalFilter`) sans avoir jamais servi.

   Aucun moteur nouveau : les étapes écrivent dans `pl_line_forecast` comme
   l'onglet Compte de résultat du hub Prévisionnel, qu'elles découpent.

   Sous-totaux : on n'affiche QUE ceux que les blocs visibles permettent de
   reconstituer. La valeur ajoutée a un sens à l'étape 1 (revenus et coûts
   directs sont tous deux à l'écran). La marge brute et l'EBITDA, non : sans les
   revenus affichés, ce sont des nombres qui tombent du ciel — le lecteur ne peut
   ni les vérifier ni les rattacher à ce qu'il saisit. Ils se lisent à l'étape Σ. */
/* UN SEUL compte de résultat, trois MODES en pilules (décision Olivier,
   2026-08-10) — il en existait trois copies dans le menu (Rapports, parcours,
   Données), une par métier ; c'est le même état financier :

     · Présenter    — défaut : lecture complète, réel + prévision, comparatif N-1
     · Éditer       — le réel : import Excel, saisie, affectation des comptes
     · Planifier    — la prévision : méthodes par ligne, génération, impôt

   Le composant ProfitLossPage savait déjà faire les trois ; seul le menu le
   découpait. Le filtrage par section reste disponible ; plus aucun écran ne
   s'en sert. */
export const FORECAST_STEPS = {
  'fc-pnl-hub':   { sections: null, subtotals: null },
};

/* Étapes côté BILAN — le tableau du bilan prévisionnel FILTRÉ sur le poste
   traité (cf. `BS_FOCUS`), pas réécrit : c'est le moteur le plus fiable du
   prévisionnel, le refaire pour le découper aurait risqué de l'abîmer.

   L'ordre suit la logique de construction : on investit, on règle le besoin en
   fonds de roulement, puis le cash flow RÉVÈLE le besoin de financement,
   auquel l'étape suivante répond — et le bilan clôt la boucle. */
export const BS_STEPS = Object.keys(BS_FOCUS);
/* Σ Plan de liquidité — méthode DIRECTE, encaissements et décaissements. Il
   PLANIFIE : c'est lui qui révèle le besoin de financement, donc il précède
   l'étape Financement.
   Σ Cash flow — méthode indirecte, dérivée du bilan. Il CONSTATE : il vient
   après le bilan, qu'il vérifie. (Ordre revu avec Olivier le 2026-08-10 : le
   tableau de flux était placé avant le financement, à la place du plan.) */
export const CF_STEP = 'fc-cash';
export const CFLOW_STEP = 'fc-cashflow';
/* Section TVA — décompte de l'année entière (réel + prévisionnel). */
export const VAT_STEP = 'fc-vat';
/* Section Charges sociales — même vue que People › Charges sociales. */
export const SOCIAL_STEP = 'fc-social';
/* Section Mouvements exceptionnels — écritures ponctuelles du plan. */
export const MOVES_STEP = 'fc-moves';
/* Section Charges variables (2026-09-29), sœur des Charges directes. */
export const VARIABLE_STEP = 'fc-variable';

export const isForecastStep = (key) =>
  !!FORECAST_STEPS[key] || !!BS_FOCUS[key] || key === CF_STEP || key === CFLOW_STEP
  || key === VAT_STEP || key === SOCIAL_STEP || key === MOVES_STEP || key === VARIABLE_STEP;

export default function ForecastStepPage({ step, activeTenant, addToast, year, onYearChange }) {
  const { t } = useTranslation();
  // Unité partagée des étapes (même mémoire par client que les hubs).
  const [unit, setUnit] = usePersistentValue(`fc_steps_unit_${activeTenant?.id}`, 'kchf');
  // Vue du Plan de liquidité (Plan · TVA · Charges sociales) : portée par la
  // page, comme l'unité — la barre standard des hubs l'affiche (2026-08-17).
  const [cashPane, setCashPane] = usePersistentValue(`fc_cash_pane_${activeTenant?.id}`, 'plan');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  /* CHARGES DIRECTES ET VARIABLES : le niveau du client (2026-09-29). Pro
     montre le réalisé devant les mois à venir ; Manuel, les seuls mois à
     venir. Plus de bascule sur la page. */
  const hasFin = planningLevel(activeTenant) === 'pro';

  const cfg = FORECAST_STEPS[step];
  if (!isForecastStep(step)) return null;

  const body = cfg ? (
    // La page porte sa propre barre de commande (vue · mode · filtres ·
    // agrégation) — c'est elle qui détient tous les états qu'elle pilote.
    <ProfitLossPage
      embedded
      commandBar
      activeTenant={activeTenant}
      addToast={addToast}
      year={year}
      onYearChange={onYearChange}
    />
  ) : step === CF_STEP ? (
    // Σ Plan de liquidité — méthode directe, uniquement l'avenir. C'est l'outil
    // de PLANIFICATION : il fait apparaître le besoin de trésorerie auquel
    // l'étape Financement répond.
    <ForecastCashPlanTab activeTenant={activeTenant} year={year} unit={unit}
      pane={cashPane} onPane={setCashPane} />
  ) : step === CFLOW_STEP ? (
    // Σ Cash flow — méthode indirecte, dérivée du bilan achevé. Il CONSTATE et
    // vérifie ; il ne se saisit pas.
    <CashFlowPage embedded actualsOnly forecastView activeTenant={activeTenant}
      addToast={addToast} year={year} />
  ) : step === VAT_STEP ? (
    // Décompte TVA de l'année : réel (compta) + prévisionnel (calculé).
    <VatYearPage activeTenant={activeTenant} year={year} unit={unit} />
  ) : step === SOCIAL_STEP ? (
    // Charges sociales : la vue de People, telle quelle (une seule vérité).
    <ForecastSocialTab activeTenant={activeTenant} year={year} unit={unit} />
  ) : step === MOVES_STEP ? (
    // Mouvements exceptionnels : ils forment le groupe « Autres mouvements ».
    <CashMovesPage activeTenant={activeTenant} year={year} unit={unit} />
  ) : step === 'fc-invest' ? (
    // Panneau en 3 cards (gabarit PlanCard de Planification).
    <InvestAmortPanel activeTenant={activeTenant} year={year} addToast={addToast} unit={unit} />
  ) : step === 'fc-stock' ? (
    // Les achats se tapent mois par mois, hors taxes (2026-09-28). La table
    // « Coût des produits vendus » et ses règles de coût, héritées de
    // l'ancien plan des ventes, sont parties le même jour : le coût vient de
    // la Trajectoire, la variation de stock se calcule.
    <PurchasesMonthlyCard activeTenant={activeTenant} year={year} unit={unit} pro={hasFin} part="direct" />
  ) : step === VARIABLE_STEP ? (
    <PurchasesMonthlyCard activeTenant={activeTenant} year={year} unit={unit} pro={hasFin} part="variable" />
  ) : (
    <ForecastBalanceSheetTab focus={step} activeTenant={activeTenant} year={year} />
  );

  /* L'UNITÉ VIT DANS LA BANDE DE PAGE (demande d'Olivier, 2026-09-28), à côté
     de l'exercice, comme partout (gabarit, registre `pageUnit`). Elle
     descendait d'une ligne, dans une barre d'onglets vide posée pour elle. */
  const hasUnit = step === 'fc-invest' || step === 'fc-stock' || step === VARIABLE_STEP || step === VAT_STEP
    || step === SOCIAL_STEP || step === MOVES_STEP || step === CF_STEP;
  const toolbar = step === CF_STEP ? (
    // Plan de liquidité : ses trois vues sur la barre standard, unité à droite.
    <Tabs
      tabs={[
        { id: 'chart', label: t('finance:cashPlan.paneChart', 'Graphique') },
        { id: 'plan', label: t('finance:cashPlan.panePlan', 'Plan') },
        { id: 'vat', label: t('finance:cashPlan.paneVat', 'TVA') },
        { id: 'social', label: t('finance:cashPlan.paneSocial', 'Charges sociales') },
      ]}
      active={cashPane}
      onChange={setCashPane}
    />
  ) : null;

  return (
    <PageShell framed title={t(`finance:forecast.steps.${step}`)} unit={hasUnit ? pageUnit : null}>
      {toolbar}
      <div className="page-shell__panel">{body}</div>
    </PageShell>
  );
}
